import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// Vertex shader for the star
const starVertexShader = `
  varying vec3 vPosition;
  varying vec3 vNormal;
  varying vec2 vUv;
  void main() {
      vPosition = position;
      vNormal = normal;
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Fragment shader for the star
const starFragmentShader = `
  uniform float time;
  uniform float intensity;
  uniform vec3 color1;
  uniform vec3 color2;
  varying vec3 vPosition;
  varying vec3 vNormal;
  varying vec2 vUv;
  float noise(vec3 p) {
      return fract(sin(dot(p, vec3(12.9898, 78.233, 54.53))) * 43758.5453);
  }
  void main() {
      vec3 pos = vPosition + time * 0.1;
      float n1 = noise(pos * 3.0);
      float n2 = noise(pos * 6.0 + vec3(100.0));
      float n3 = noise(pos * 12.0 + vec3(200.0));
      float pattern = n1 * 0.5 + n2 * 0.3 + n3 * 0.2;
      pattern = pow(pattern, 2.0);
      vec3 finalColor = mix(color1, color2, pattern);
      finalColor *= (1.0 + intensity * pattern * 2.0);
      float fresnel = pow(1.0 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
      finalColor += fresnel * intensity * 0.5;
      gl_FragColor = vec4(finalColor, 1.0);
  }
`;

// Vertex shader for planets
const planetVertexShader = `
  varying vec3 vPosition;
  varying vec3 vNormal;
  varying vec2 vUv;
  void main() {
      vPosition = position;
      vNormal = normalize(normalMatrix * normal);
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Fragment shader for planets
const planetFragmentShader = `
  uniform float time;
  uniform vec3 baseColor;
  uniform vec3 accentColor;
  uniform float energy;
  varying vec3 vPosition;
  varying vec3 vNormal;
  varying vec2 vUv;
  float noise(vec2 p) {
      return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
  }
  void main() {
      vec2 uv = vUv + time * 0.02;
      float n1 = noise(uv * 8.0);
      float n2 = noise(uv * 16.0);
      float pattern = n1 * 0.7 + n2 * 0.3;
      vec3 color = mix(baseColor, accentColor, pattern);
      float fresnel = pow(1.0 - abs(dot(vNormal, vec3(0.0, 0.0, 1.0))), 1.5);
      color += fresnel * accentColor * 0.5;
      color *= (1.0 + energy * 0.8);
      gl_FragColor = vec4(color, 1.0);
  }
`;

// Vertex shader for arcs
const arcVertexShader = `
  varying vec2 vUv;
  varying vec3 vPosition;
  void main() {
      vUv = uv;
      vPosition = position;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Fragment shader for arcs
const arcFragmentShader = `
  uniform float time;
  uniform vec3 color;
  uniform float opacity;
  uniform float energy;
  varying vec2 vUv;
  varying vec3 vPosition;
  void main() {
      float flow = abs(sin(vUv.x * 15.0 - time * 12.0));
      float pulse = sin(time * 8.0) * 0.5 + 0.5;
      float pattern = pow(flow, 1.5) * (1.0 + pulse * energy);
      float fade = sin(vUv.x * 3.14159);
      vec3 finalColor = color * (pattern * 2.0 + 0.3);
      float alpha = fade * opacity * (pattern + 0.2) * (1.0 + energy);
      gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Define theme types
interface Theme {
  name: string;
  starColors: { color1: number; color2: number };
  planetData: Array<{
    baseColor: [number, number, number];
    accentColor: [number, number, number];
    trailColor: number;
  }>;
  ambientLightColor: number;
  starLightColor: number;
  directionalLights: { color1: number; color2: number };
  metalMaterialColor: number;
  ringColor: number;
  arcColor: number;
}

// Define planet data type
interface PlanetData {
  group: THREE.Group;
  body: THREE.Mesh;
  ring: THREE.Mesh;
  material: THREE.ShaderMaterial;
}

// Define particle system type
interface ParticleSystem {
  system: THREE.Points;
  planet: THREE.Mesh;
  positions: Float32Array;
  currentIndex: number;
  radius: number;
}

// Define effect types
interface Effect {
  type: string;
  startTime: number;
  duration: number;
  update: (elapsed: number, delta?: number) => void;
  end: () => void;
  mesh?: THREE.Mesh | null;
  material?: THREE.Material;
  obj1?: THREE.Object3D;
  obj2?: THREE.Object3D;
}

interface ThreeVisualizationProps {
  className?: string;
  listening?: boolean;
  volume?: number;
  hideVisualization?: boolean;
}

const ThreeVisualization: React.FC<ThreeVisualizationProps> = ({
  className,
  listening = false,
  volume = 0,
  hideVisualization = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const composerRef = useRef<EffectComposer | null>(null);
  const clockRef = useRef<THREE.Clock | null>(null);
  
  const starRef = useRef<THREE.Mesh | null>(null);
  const orreryRef = useRef<THREE.Group | null>(null);
  const starLightRef = useRef<THREE.PointLight | null>(null);
  const ambientLightRef = useRef<THREE.AmbientLight | null>(null);
  const blueLightRef = useRef<THREE.DirectionalLight | null>(null);
  const purpleLightRef = useRef<THREE.DirectionalLight | null>(null);
  const metalMaterialRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const ringMaterialRef = useRef<THREE.MeshBasicMaterial | null>(null);
  
  const planetsRef = useRef<PlanetData[]>([]);
  const activeEffectsRef = useRef<Effect[]>([]);
  const particleSystemsRef = useRef<ParticleSystem[]>([]);
  
  const timeAccelerationRef = useRef<number>(1.0);
  const isResonanceActiveRef = useRef<boolean>(false);
  const currentThemeIndexRef = useRef<number>(0);
  const isMorphedRef = useRef<boolean>(false);
  const animationFrameIdRef = useRef<number | null>(null);

  // Define themes - inactive (dark blue) and active (bright yellow)
  const themes: Theme[] = useMemo(() => [
    { 
      name: 'Inactive',
      starColors: { color1: 0x1a3a52, color2: 0x0d1f2d },
      planetData: [
        { baseColor: [0.1, 0.2, 0.4], accentColor: [0.2, 0.3, 0.6], trailColor: 0x1a3a52 },
        { baseColor: [0.1, 0.15, 0.35], accentColor: [0.15, 0.25, 0.5], trailColor: 0x0d1f2d },
        { baseColor: [0.05, 0.1, 0.3], accentColor: [0.1, 0.2, 0.45], trailColor: 0x0a1929 }
      ],
      ambientLightColor: 0x0a1929,
      starLightColor: 0x1a3a52,
      directionalLights: { color1: 0x0d1f2d, color2: 0x0a1929 },
      metalMaterialColor: 0x0a1929,
      ringColor: 0x1a3a52,
      arcColor: 0x0d1f2d,
    },
    { 
      name: 'Active',
      starColors: { color1: 0xffffcc, color2: 0xffff00 },
      planetData: [
        { baseColor: [1.0, 1.0, 0.6], accentColor: [1.0, 1.0, 0.2], trailColor: 0xffff00 },
        { baseColor: [1.0, 0.9, 0.4], accentColor: [1.0, 0.95, 0.1], trailColor: 0xffffcc },
        { baseColor: [0.95, 0.95, 0.3], accentColor: [1.0, 1.0, 0.0], trailColor: 0xffff99 }
      ],
      ambientLightColor: 0x333300,
      starLightColor: 0xffffcc,
      directionalLights: { color1: 0xffff00, color2: 0xffff99 },
      metalMaterialColor: 0x333300,
      ringColor: 0xffffcc,
      arcColor: 0xffff00,
    }
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to get cube position
  const getCubePosition = (p: THREE.Vector3): THREE.Vector3 => {
    const v = p.clone().normalize();
    const max = Math.max(Math.abs(v.x), Math.abs(v.y), Math.abs(v.z));
    return v.divideScalar(max);
  };

  // Helper function to get square orbit positions
  const getSquareOrbitPositions = (radius: number, tubularSegments: number, radialSegments: number): Float32Array => {
    const positions = new Float32Array((radialSegments + 1) * (tubularSegments + 1) * 3);
    let posIdx = 0;
    const sideLength = radius * 2;
    const perimeter = sideLength * 4;

    for (let i = 0; i <= radialSegments; i++) {
      const u = i / radialSegments;
      const p = u * perimeter;
      
      const center = new THREE.Vector3();
      const tangent = new THREE.Vector3();

      if (p < sideLength) { 
        center.set(radius, -radius + p, 0);
        tangent.set(0, 1, 0);
      } else if (p < sideLength * 2) { 
        center.set(radius - (p - sideLength), radius, 0);
        tangent.set(-1, 0, 0);
      } else if (p < sideLength * 3) { 
        center.set(-radius, radius - (p - sideLength * 2), 0);
        tangent.set(0, -1, 0);
      } else { 
        center.set(-radius + (p - sideLength * 3), -radius, 0);
        tangent.set(1, 0, 0);
      }

      const normal = new THREE.Vector3(0, 0, 1);
      const binormal = new THREE.Vector3().crossVectors(tangent, normal).normalize();
      
      for (let j = 0; j <= tubularSegments; j++) {
        const v = j / tubularSegments * Math.PI * 2;
        const tubeRadius = 0.08;

        const cx = -tubeRadius * Math.cos(v);
        const cy = tubeRadius * Math.sin(v);

        const pos = center.clone();
        pos.x += cx * binormal.x;
        pos.y += cx * binormal.y;
        pos.z += cy;

        positions[posIdx++] = pos.x;
        positions[posIdx++] = pos.y;
        positions[posIdx++] = pos.z;
      }
    }
    return positions;
  };

  // Initialize the scene
  const init = useCallback(() => {
    if (!containerRef.current) return;

    // Create scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    
    // Create clock
    const clock = new THREE.Clock();
    clockRef.current = clock;
    
    // Create camera
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 3000);
    camera.position.set(25, 20, 25);
    cameraRef.current = camera;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      powerPreference: "high-performance" 
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.03;
    controls.minDistance = 8;
    controls.maxDistance = 150;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.15;
    controls.enablePan = false;
    controlsRef.current = controls;
    
    // Setup lighting
    setupLighting();
    
    // Create orrery
    createOrrery();
    
    // Create environment
    createEnvironment();
    
    // Setup post processing
    setupPostProcessing();
    
    // Apply theme based on listening state
    applyTheme(listening ? 1 : 0);

    // Handle window resize
    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current || !composerRef.current) return;
      
      cameraRef.current.aspect = window.innerWidth / window.innerHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
      composerRef.current.setSize(window.innerWidth, window.innerHeight);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Start animation
    animate();
    
    // Cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      if (containerRef.current && rendererRef.current?.domElement) {
        containerRef.current.removeChild(rendererRef.current.domElement);
      }
      // Dispose of Three.js objects
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      if (composerRef.current) {
        composerRef.current.dispose();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Setup lighting
  const setupLighting = () => {
    if (!sceneRef.current) return;
    
    const ambientLight = new THREE.AmbientLight(0x0a1929, 0.8);
    sceneRef.current.add(ambientLight);
    ambientLightRef.current = ambientLight;
    
    const starLight = new THREE.PointLight(0x1a3a52, 3, 120, 1.8);
    starLight.castShadow = true;
    starLight.shadow.mapSize.width = 2048;
    starLight.shadow.mapSize.height = 2048;
    sceneRef.current.add(starLight);
    starLightRef.current = starLight;

    const blueLight = new THREE.DirectionalLight(0x0d1f2d, 0.5);
    blueLight.position.set(-50, 30, -30);
    sceneRef.current.add(blueLight);
    blueLightRef.current = blueLight;

    const purpleLight = new THREE.DirectionalLight(0x0a1929, 0.3);
    purpleLight.position.set(30, -20, 50);
    sceneRef.current.add(purpleLight);
    purpleLightRef.current = purpleLight;
  };

  // Setup post processing
  const setupPostProcessing = () => {
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;
    
    const composer = new EffectComposer(rendererRef.current);
    composer.addPass(new RenderPass(sceneRef.current, cameraRef.current));
    
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight), 
      0.6, 
      0.5, 
      0.15 
    );
    composer.addPass(bloomPass);
    
    composer.addPass(new OutputPass());
    composerRef.current = composer;
  };

  // Create orrery
  const createOrrery = () => {
    if (!sceneRef.current) return;
    
    const orrery = new THREE.Group();
    sceneRef.current.add(orrery);
    orreryRef.current = orrery;

    const metalMaterial = new THREE.MeshStandardMaterial({
      color: 0x0a1929,
      metalness: 0.95,
      roughness: 0.2,
      emissive: 0x0a1929,
      emissiveIntensity: 0.4,
      envMapIntensity: 1.5
    });
    metalMaterialRef.current = metalMaterial;

    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0x1a3a52,
      wireframe: true,
      transparent: true,
      opacity: 0.4
    });
    ringMaterialRef.current = ringMaterial;
    
    // Create star
    const starGeo = new THREE.IcosahedronGeometry(2.2, 5);
    const starMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0.0 },
        intensity: { value: 1.0 },
        color1: { value: new THREE.Color(0x1a3a52) },
        color2: { value: new THREE.Color(0x0d1f2d) }
      },
      vertexShader: starVertexShader,
      fragmentShader: starFragmentShader
    });
    const star = new THREE.Mesh(starGeo, starMaterial);
    star.castShadow = false;
    star.receiveShadow = false;
    orrery.add(star);
    starRef.current = star;

    // Store original and target positions for morphing
    const starOriginalPos = star.geometry.attributes.position.clone();
    const starTargetPos = new Float32Array(starOriginalPos.count * 3);
    for (let i = 0; i < starOriginalPos.count; i++) {
      const p = new THREE.Vector3().fromBufferAttribute(starOriginalPos, i);
      const cubeP = getCubePosition(p).multiplyScalar(2.2);
      starTargetPos.set([cubeP.x, cubeP.y, cubeP.z], i * 3);
    }
    star.userData.originalPositions = starOriginalPos;
    star.userData.targetPositions = new THREE.BufferAttribute(starTargetPos, 3);

    // Create gears
    // for (let i = 0; i < 7; i++) {
    //   const gearGeo = new THREE.TorusGeometry(2.8 + i * 0.5, 0.18, 12, 64);
    //   const gear = new THREE.Mesh(gearGeo, metalMaterial);
    //   gear.rotation.x = Math.PI / 2;
    //   gear.position.y = -2 - i * 0.25;
    //   gear.userData.rotationSpeed = (i % 2 === 0 ? 1 : -1) * (0.08 + i * 0.04);
    //   gear.castShadow = true;
    //   gear.receiveShadow = true;
    //   orrery.add(gear);
    // }

    // Create planets
    const planetGeometries = [
      new THREE.OctahedronGeometry(0.6, 3),
      new THREE.DodecahedronGeometry(0.9, 2),
      new THREE.IcosahedronGeometry(0.7, 3)
    ];
    const planetBaseData = [
      { size: 0.6, distance: 8, speed: 0.6 },
      { size: 0.9, distance: 14, speed: 0.35 },
      { size: 0.7, distance: 22, speed: 0.25 }
    ];

    planetBaseData.forEach((data, i) => {
      const planetGroup = new THREE.Group();
      planetGroup.userData.orbitSpeed = data.speed;
      planetGroup.rotation.y = Math.random() * Math.PI * 2;
      orrery.add(planetGroup);

      const radialSegments = 128;
      const tubularSegments = 20;
      const ringGeo = new THREE.TorusGeometry(data.distance, 0.08, tubularSegments, radialSegments);
      const ring = new THREE.Mesh(ringGeo, ringMaterial);
      ring.rotation.x = Math.PI / 2;
      planetGroup.add(ring);

      // Store original and target positions for morphing
      ring.userData.originalPositions = ring.geometry.attributes.position.clone();
      const squarePositions = getSquareOrbitPositions(data.distance, tubularSegments, radialSegments);
      ring.userData.targetPositions = new THREE.BufferAttribute(squarePositions, 3);
      
      const planetMaterial = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0.0 },
          baseColor: { value: new THREE.Vector3(0.1, 0.2, 0.4) },
          accentColor: { value: new THREE.Vector3(0.2, 0.3, 0.6) },
          energy: { value: 0.0 }
        },
        vertexShader: planetVertexShader,
        fragmentShader: planetFragmentShader
      });
      
      const planet = new THREE.Mesh(planetGeometries[i], planetMaterial);
      planet.position.x = data.distance;
      planet.userData.selfRotation = 0.6;
      planet.castShadow = true;
      planet.receiveShadow = true;
      planetGroup.add(planet);

      // Store original and target positions for morphing
      const planetOriginalPos = planet.geometry.attributes.position.clone();
      const planetTargetPos = new Float32Array(planetOriginalPos.count * 3);
      for (let j = 0; j < planetOriginalPos.count; j++) {
        const p = new THREE.Vector3().fromBufferAttribute(planetOriginalPos, j);
        const cubeP = getCubePosition(p).multiplyScalar(data.size);
        planetTargetPos.set([cubeP.x, cubeP.y, cubeP.z], j * 3);
      }
      planet.userData.originalPositions = planetOriginalPos;
      planet.userData.targetPositions = new THREE.BufferAttribute(planetTargetPos, 3);

      createParticleTrail(planet, 0x1a3a52, data.distance);

      planetsRef.current.push({
        group: planetGroup,
        body: planet,
        ring: ring,
        material: planetMaterial
      });
    });
  };

  // Create particle trail
  const createParticleTrail = (planet: THREE.Mesh, color: number, radius: number) => {
    if (!orreryRef.current) return;
    
    const particleCount = 50;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);

    const colorObj = new THREE.Color(color);
    
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = Math.sin(angle) * radius;
      
      colors[i * 3] = colorObj.r;
      colors[i * 3 + 1] = colorObj.g;
      colors[i * 3 + 2] = colorObj.b;
      
      sizes[i] = Math.random() * 0.5 + 0.1;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const material = new THREE.PointsMaterial({
      size: 0.3,
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true
    });

    const particles = new THREE.Points(geometry, material);
    planet.parent?.add(particles);
    
    particleSystemsRef.current.push({
      system: particles,
      planet: planet,
      positions: positions,
      currentIndex: 0,
      radius: radius
    });
  };

  // Create environment
  const createEnvironment = () => {
    if (!sceneRef.current) return;
    
    const layers = [
      { count: 3000, distance: [600, 1000], size: [0.8, 1.5], color: 0x1a3a52 },
      { count: 2000, distance: [1000, 1500], size: [1.0, 2.0], color: 0x0d1f2d },
      { count: 1000, distance: [1500, 2000], size: [1.5, 3.0], color: 0x0a1929 }
    ];

    layers.forEach(layer => {
      const positions = new Float32Array(layer.count * 3);
      const colors = new Float32Array(layer.count * 3);
      const sizes = new Float32Array(layer.count);
      
      const color = new THREE.Color(layer.color);
      
      for (let i = 0; i < layer.count; i++) {
        const u = Math.random();
        const v = Math.random();
        const theta = 2 * Math.PI * u;
        const phi = Math.acos(2 * v - 1);
        const radius = layer.distance[0] + Math.random() * (layer.distance[1] - layer.distance[0]);
        
        positions[i*3] = radius * Math.sin(phi) * Math.cos(theta);
        positions[i*3+1] = radius * Math.sin(phi) * Math.sin(theta);
        positions[i*3+2] = radius * Math.cos(phi);
        
        colors[i*3] = color.r;
        colors[i*3+1] = color.g;
        colors[i*3+2] = color.b;
        
        sizes[i] = layer.size[0] + Math.random() * (layer.size[1] - layer.size[0]);
      }
      
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
      
      const material = new THREE.PointsMaterial({
        size: 2.0,
        vertexColors: true,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.8
      });
      
      const starfield = new THREE.Points(geometry, material);
      sceneRef.current?.add(starfield);
    });
  };

  // Activate resonance effect
  const activateResonance = useCallback(() => {
    if (isResonanceActiveRef.current || !clockRef.current || !starRef.current || !starLightRef.current || !metalMaterialRef.current) return;
    
    isResonanceActiveRef.current = true;

    const activationEffect: Effect = {
      type: 'activation',
      startTime: clockRef.current.getElapsedTime(),
      duration: 8.0,
      update: (elapsed) => {
        const progress = elapsed / activationEffect.duration;
        const intensity = Math.sin(progress * Math.PI) * 3.0;
        
        if (starLightRef.current) starLightRef.current.intensity = 3.0 + intensity * 3.0;
        if (starRef.current) {
          const material = starRef.current.material as THREE.ShaderMaterial;
          if (material && material.uniforms) material.uniforms.intensity.value = 1.0 + intensity * 2.0;
        }
        if (metalMaterialRef.current) metalMaterialRef.current.emissiveIntensity = 0.4 + intensity * 2.0;

        planetsRef.current.forEach(p => {
          p.group.userData.orbitSpeed = p.body.userData.baseOrbitSpeed * (1.0 + intensity * 1.5);
          if (p.material && p.material.uniforms) {
            p.material.uniforms.energy.value = intensity;
          }
        });
      },
      end: () => {
        if (starLightRef.current) starLightRef.current.intensity = 3.0;
        if (starRef.current) {
          const material = starRef.current.material as THREE.ShaderMaterial;
          if (material && material.uniforms) material.uniforms.intensity.value = 1.0;
        }
        if (metalMaterialRef.current) metalMaterialRef.current.emissiveIntensity = 0.4;
        planetsRef.current.forEach(p => {
          p.group.userData.orbitSpeed = p.body.userData.baseOrbitSpeed;
          if (p.material && p.material.uniforms) {
            p.material.uniforms.energy.value = 0.0;
          }
        });
        isResonanceActiveRef.current = false;
      }
    };
    
    planetsRef.current.forEach(p => {
      p.body.userData.baseOrbitSpeed = p.group.userData.orbitSpeed;
    });

    activeEffectsRef.current.push(activationEffect);
    
    for (let i = 0; i < planetsRef.current.length; i++) {
      const p1 = planetsRef.current[i].body;
      const p2 = i === 0 ? starRef.current! : planetsRef.current[i-1].body;
      createEnhancedArc(p1, p2, 6.0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Create enhanced arc
  const createEnhancedArc = (obj1: THREE.Object3D, obj2: THREE.Object3D, duration: number) => {
    if (!sceneRef.current || !clockRef.current) return;
    
    const currentTheme = themes[currentThemeIndexRef.current];
    const arcMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0.0 },
        color: { value: new THREE.Color(currentTheme.arcColor) },
        opacity: { value: 0.0 },
        energy: { value: 0.0 }
      },
      vertexShader: arcVertexShader,
      fragmentShader: arcFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const arcEffect: Effect = {
      type: 'arc',
      mesh: null,
      material: arcMaterial,
      startTime: clockRef.current.getElapsedTime(),
      duration: duration,
      obj1: obj1,
      obj2: obj2,
      update: (elapsed) => {
        const progress = elapsed / arcEffect.duration;
        if (progress > 1) {
          if (arcEffect.mesh) arcEffect.mesh.visible = false;
          return;
        }

        const p1 = new THREE.Vector3();
        const p2 = new THREE.Vector3();
        arcEffect.obj1!.getWorldPosition(p1);
        arcEffect.obj2!.getWorldPosition(p2);
        
        const midPoint = new THREE.Vector3().lerpVectors(p1, p2, 0.5);
        const controlPoint = midPoint.clone().add(new THREE.Vector3(0, p1.distanceTo(p2) * 0.5, 0));
        const curve = new THREE.QuadraticBezierCurve3(p1, controlPoint, p2);

        const tubeGeo = new THREE.TubeGeometry(curve, 48, 0.15, 12, false);

        if (!arcEffect.mesh) {
          arcEffect.mesh = new THREE.Mesh(tubeGeo, arcMaterial);
          sceneRef.current?.add(arcEffect.mesh);
        } else {
          arcEffect.mesh.geometry.dispose();
          arcEffect.mesh.geometry = tubeGeo;
        }

        const intensity = Math.sin(progress * Math.PI);
        if (arcMaterial.uniforms) {
          arcMaterial.uniforms.opacity.value = intensity * 0.9;
          arcMaterial.uniforms.energy.value = intensity * 2.0;
          arcMaterial.uniforms.time.value = clockRef.current?.getElapsedTime() || 0;
        }
        if (arcEffect.mesh) arcEffect.mesh.visible = true;
      },
      end: () => {
        if (arcEffect.mesh) {
          sceneRef.current?.remove(arcEffect.mesh);
          arcEffect.mesh.geometry.dispose();
          arcMaterial.dispose();
        }
      }
    };
    activeEffectsRef.current.push(arcEffect);
  };

  // Toggle morph
  const toggleMorph = useCallback(() => {
    if (!clockRef.current) return;
    
    const morphEffect: Effect = {
      type: 'morph',
      startTime: clockRef.current.getElapsedTime(),
      duration: 2.0,
      update: (elapsed) => {
        let progress = elapsed / morphEffect.duration;
        if (progress > 1) progress = 1;

        const easedProgress = 0.5 - 0.5 * Math.cos(progress * Math.PI);

        const morphProgress = isMorphedRef.current ? 1.0 - easedProgress : easedProgress;

        const morphables = [starRef.current, ...planetsRef.current.map(p => p.body), ...planetsRef.current.map(p => p.ring)].filter(Boolean) as THREE.Mesh[];

        morphables.forEach(obj => {
          const original = obj.userData.originalPositions;
          const target = obj.userData.targetPositions;
          const current = obj.geometry.attributes.position;

          for (let i = 0; i < original.count; i++) {
            const ox = original.getX(i);
            const oy = original.getY(i);
            const oz = original.getZ(i);
            const tx = target.getX(i);
            const ty = target.getY(i);
            const tz = target.getZ(i);
            
            current.setXYZ(
              i,
              THREE.MathUtils.lerp(ox, tx, morphProgress),
              THREE.MathUtils.lerp(oy, ty, morphProgress),
              THREE.MathUtils.lerp(oz, tz, morphProgress)
            );
          }
          current.needsUpdate = true;
          obj.geometry.computeVertexNormals();
        });
      },
      end: () => {
        isMorphedRef.current = !isMorphedRef.current;
      }
    };
    activeEffectsRef.current.push(morphEffect);
  }, []);

  // Reset view
  const resetView = () => {
    if (controlsRef.current && cameraRef.current) {
      controlsRef.current.reset();
      cameraRef.current.position.set(25, 20, 25);
      controlsRef.current.update();
    }
  };

  // Toggle time acceleration
  const toggleTimeAcceleration = () => {
    timeAccelerationRef.current = timeAccelerationRef.current === 1.0 ? 3.0 : 
                                 timeAccelerationRef.current === 3.0 ? 0.5 : 1.0;
  };

  // Toggle theme
  const toggleTheme = () => {
    currentThemeIndexRef.current = (currentThemeIndexRef.current + 1) % themes.length;
    applyTheme(currentThemeIndexRef.current);
  };

  // Apply theme
  const applyTheme = useCallback((themeIndex: number) => {
    const theme = themes[themeIndex];
    
    if (starRef.current) {
      const material = starRef.current.material as THREE.ShaderMaterial;
      if (material && material.uniforms) {
        material.uniforms.color1.value.set(theme.starColors.color1);
        material.uniforms.color2.value.set(theme.starColors.color2);
      }
    }

    planetsRef.current.forEach((p, i) => {
      if (i < theme.planetData.length && p && p.material && p.material.uniforms) {
        const planetTheme = theme.planetData[i];
        p.material.uniforms.baseColor.value.set(...planetTheme.baseColor);
        p.material.uniforms.accentColor.value.set(...planetTheme.accentColor);
      }
    });

    particleSystemsRef.current.forEach((ps, i) => {
      if (i < theme.planetData.length) {
        const trailColor = new THREE.Color(theme.planetData[i].trailColor);
        const colors = ps.system.geometry.attributes.color;
        for (let j = 0; j < colors.count; j++) {
          colors.setXYZ(j, trailColor.r, trailColor.g, trailColor.b);
        }
        colors.needsUpdate = true;
      }
    });

    if (ambientLightRef.current) ambientLightRef.current.color.set(theme.ambientLightColor);
    if (starLightRef.current) starLightRef.current.color.set(theme.starLightColor);
    if (blueLightRef.current) blueLightRef.current.color.set(theme.directionalLights.color1);
    if (purpleLightRef.current) purpleLightRef.current.color.set(theme.directionalLights.color2);

    if (metalMaterialRef.current) metalMaterialRef.current.color.set(theme.metalMaterialColor);
    if (ringMaterialRef.current) ringMaterialRef.current.color.set(theme.ringColor);
  }, [themes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Animation loop
  const animate = () => {
    animationFrameIdRef.current = requestAnimationFrame(animate);
    
    if (!clockRef.current || !starRef.current || !orreryRef.current || !controlsRef.current || !composerRef.current) return;
    
    const delta = clockRef.current.getDelta() * timeAccelerationRef.current;
    const time = clockRef.current.getElapsedTime();

    // Rotate star
    starRef.current.rotation.y += delta * 0.3;
    starRef.current.rotation.x += delta * 0.15;
    const starMaterial = starRef.current.material as THREE.ShaderMaterial;
    if (starMaterial && starMaterial.uniforms) {
      starMaterial.uniforms.time.value = time;
    }
    
    // Rotate gears
    orreryRef.current.children.forEach(child => {
      if (child.userData.rotationSpeed) {
        child.rotation.z += delta * child.userData.rotationSpeed;
      }
    });
    
    // Rotate planets
    planetsRef.current.forEach(p => {
      p.group.rotation.y += delta * p.group.userData.orbitSpeed;
      p.body.rotation.y += delta * p.body.userData.selfRotation;
      p.body.rotation.z += delta * p.body.userData.selfRotation * 0.3;
      if (p.material && p.material.uniforms) {
        p.material.uniforms.time.value = time;
      }
    });

    // Update particle systems
    particleSystemsRef.current.forEach(ps => {
      ps.system.geometry.attributes.position.needsUpdate = true;
    });

    // Update effects
    for (let i = activeEffectsRef.current.length - 1; i >= 0; i--) {
      const effect = activeEffectsRef.current[i];
      const elapsed = time - effect.startTime;
      if (elapsed > effect.duration) {
        effect.end();
        activeEffectsRef.current.splice(i, 1);
      } else {
        effect.update(elapsed, delta);
      }
    }

    // Update controls and render
    controlsRef.current.update();
    composerRef.current.render(delta);
  };

  // Initialize on mount
  useEffect(() => {
    const cleanup = init();
    return cleanup;
  }, [init]);

  // Handle listening state changes
  useEffect(() => {
    // Apply theme based on listening state
    applyTheme(listening ? 1 : 0);
    
    // Toggle morph based on listening state
    if (listening !== isMorphedRef.current) {
      toggleMorph();
    }
    
    if (listening) {
      activateResonance();
    }
  }, [listening, activateResonance, applyTheme, toggleMorph]);

  // Handle volume changes
  useEffect(() => {
    // We can use volume to adjust the intensity of effects
    // For example, we could make the star pulse with the volume
    if (starRef.current) {
      const material = starRef.current.material as THREE.ShaderMaterial;
      if (material && material.uniforms) {
        material.uniforms.intensity.value = 1.0 + volume * 0.5;
      }
    }
  }, [volume]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: 'radial-gradient(circle at center, #0a0f1a 0%, #020408 100%)',
        display: hideVisualization ? 'none' : 'block'
      }}
    >
      {/* Stats display */}
      {/* <div 
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '10px',
          fontWeight: '400',
          fontFamily: 'Inter, sans-serif',
          pointerEvents: 'none',
          zIndex: 5,
          textShadow: '0 0 10px rgba(0, 0, 0, 0.8)',
          lineHeight: '1.6',
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px) saturate(150%)',
          padding: '10px 14px',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
        }}
      >
        <div>Systems: {listening ? 'Active' : 'Idle'}</div>
        <div>Resonance: {listening ? 'Stable' : 'Low'}</div>
        <div>Phase: {listening ? 'Nominal' : 'Standby'}</div>
        <div>Shape: {isMorphedRef.current ? 'Rectangular' : 'Circular'}</div>
      </div> */}
      
      {/* Control buttons - Hidden */}
      <div 
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
          display: 'none', // Hide all buttons
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '12px',
          alignItems: 'center',
          padding: '8px',
          background: 'rgba(255, 255, 255, 0.01)',
          backdropFilter: 'blur(40px) saturate(180%)',
          borderRadius: '20px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1)'
        }}
      >
        <button 
          onClick={resetView}
          style={{
            position: 'relative',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '12px',
            padding: '10px 18px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '12px',
            fontWeight: '500',
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            userSelect: 'none',
            letterSpacing: '0.5px',
            textTransform: 'none',
            minWidth: '100px',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexShrink: 0,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.15), inset 0 -1px 0 rgba(0, 0, 0, 0.1)'
          }}
        >
          <span></span><span></span><span></span><span></span>
          <span>Reset View</span>
        </button>
        
        <button 
          onClick={activateResonance}
          style={{
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(255, 100, 100, 0.15) 0%, rgba(255, 50, 50, 0.1) 100%)',
            border: '1px solid rgba(255, 100, 100, 0.3)',
            borderRadius: '12px',
            padding: '10px 18px',
            color: 'rgba(255, 150, 150, 0.95)',
            fontSize: '12px',
            fontWeight: '500',
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            userSelect: 'none',
            letterSpacing: '0.5px',
            textTransform: 'none',
            minWidth: '100px',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexShrink: 0,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.15), inset 0 -1px 0 rgba(0, 0, 0, 0.1)'
          }}
        >
          <span></span><span></span><span></span><span></span>
          <span>Activate Resonance</span>
        </button>
        
        <button 
          onClick={toggleTimeAcceleration}
          style={{
            position: 'relative',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '12px',
            padding: '10px 18px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '12px',
            fontWeight: '500',
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            userSelect: 'none',
            letterSpacing: '0.5px',
            textTransform: 'none',
            minWidth: '100px',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexShrink: 0,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.15), inset 0 -1px 0 rgba(0, 0, 0, 0.1)'
          }}
        >
          <span></span><span></span><span></span><span></span>
          <span>Time: {timeAccelerationRef.current}x</span>
        </button>
        
        <button 
          onClick={toggleTheme}
          style={{
            position: 'relative',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '12px',
            padding: '10px 18px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '12px',
            fontWeight: '500',
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            userSelect: 'none',
            letterSpacing: '0.5px',
            textTransform: 'none',
            minWidth: '100px',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexShrink: 0,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.15), inset 0 -1px 0 rgba(0, 0, 0, 0.1)'
          }}
        >
          <span></span><span></span><span></span><span></span>
          <span>Theme: {themes[currentThemeIndexRef.current].name}</span>
        </button>
        
        <button 
          onClick={toggleMorph}
          style={{
            position: 'relative',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '12px',
            padding: '10px 18px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '12px',
            fontWeight: '500',
            fontFamily: 'Inter, sans-serif',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            userSelect: 'none',
            letterSpacing: '0.5px',
            textTransform: 'none',
            minWidth: '100px',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexShrink: 0,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.15), inset 0 -1px 0 rgba(0, 0, 0, 0.1)'
          }}
        >
          <span></span><span></span><span></span><span></span>
          <span>Morph Shape</span>
        </button>
      </div>
    </div>
  );
};

export default ThreeVisualization;